import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { buildUrl, fetchJson } from "../shared/api";
import { Markdown, normalizeMarkdown } from "../shared/markdown";
import { flagBadge, effectiveFlag } from "../shared/releaseNotes";
import type { ReleaseNote } from "../shared/types/api";

function ReleaseNotesPage() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const moduleVersion = params.get("module-version");

    //const navigate = useNavigate();

    const [releaseNote, setReleaseNote] = useState<ReleaseNote | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!moduleVersion) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        (async () => {
            try {
                const data = await fetchJson<ReleaseNote>(
                    buildUrl("/release-note", { moduleVersion })
                );
                if (!cancelled) setReleaseNote(data);
            } catch (err: unknown) {
                if (!cancelled) setError(t("common.noData"));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [moduleVersion, t, i18n.language]);

    //const handleBack = () => navigate("/");

    const normalizedDescription = useMemo(
        () => normalizeMarkdown(releaseNote?.description ?? ""),
        [releaseNote?.description]
    );

    return (
        <div className="page-card">
            <h2 className="page-title">
                <FontAwesomeIcon icon={faFileLines} /> {t("releaseNotes.title")}
            </h2>

            <div style={{ fontSize: "16px", lineHeight: "1.8", color: "#2563eb" }}>

                {isLoading && <p>{t("releaseNotes.loading")}</p>}
                {error && <p className="text-error">{error}</p>}


                {releaseNote && (
                    <div className="meta-block">
                        <h3 className="page-title">
                            {releaseNote.tag_name}
                            {flagBadge(effectiveFlag(releaseNote.flag, releaseNote.tag_name)) && (
                                <span className="release-flag"> {flagBadge(effectiveFlag(releaseNote.flag, releaseNote.tag_name))}</span>
                            )}
                        </h3>
                        {releaseNote?.notLocalized && <p className="text-error"><strong>{t("releaseNotes.notLocalized", { language: releaseNote.fallbackLanguage})}</strong></p> }
                        <p>
                            <strong>{t("releaseNotes.repository")}:</strong> {releaseNote.tableName ?? releaseNote.repository}
                        </p>
                        <p>
                            <strong>{t("releaseNotes.releaseDate")}:</strong>{" "}
                            {new Date(releaseNote.released_at).toLocaleDateString(i18n.language || "cs-CZ")}
                        </p>

                        <div className="detail-box detail-box--spaced">
                            <Markdown>
                                {normalizedDescription || t("releaseNotes.noDescription")}
                            </Markdown>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReleaseNotesPage;